import { useEffect, useState } from 'react'
import dragIcon from '@/assets/drag-icon.svg'
import { ConfirmationModal } from '@/components/ui/confirmation-modal'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import type { Collection } from '@/services/products/types'
import { type DeploymentType, DeploymentTypeCards } from './deploymentOptions'

const noFeedValue = '__no_feed__'

export interface CreateAgentFormData {
  instructions: string
  deploymentType: DeploymentType
  collectionId?: string
}

interface CreateAgentModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (data: CreateAgentFormData) => void | Promise<void>
  isLoading?: boolean
  collections?: Collection[]
}

export function CreateAgentModal({
  open,
  onOpenChange,
  onConfirm,
  isLoading = false,
  collections,
}: CreateAgentModalProps) {
  const [formData, setFormData] = useState<{
    instructions: string
    deploymentType: DeploymentType | null
    collectionId?: string
  }>({
    instructions: '',
    deploymentType: null,
  })

  useEffect(() => {
    if (
      open &&
      collections &&
      collections.length > 0 &&
      !formData.collectionId
    ) {
      const defaultCollection =
        collections.find((c) => c.isDefault) || collections[0]
      if (defaultCollection) {
        setFormData((prev) => ({
          ...prev,
          collectionId: defaultCollection.id,
        }))
      }
    }
  }, [open, collections, formData.collectionId])

  const handleSubmit = async () => {
    if (formData.deploymentType === null) return

    try {
      await onConfirm({
        instructions: formData.instructions,
        deploymentType: formData.deploymentType,
        collectionId:
          formData.collectionId === noFeedValue
            ? undefined
            : formData.collectionId,
      })
      setFormData({
        instructions: '',
        deploymentType: null,
        collectionId: undefined,
      })
    } catch (error) {
      console.error('Submission failed:', error)
    }
  }

  const handleClose = () => {
    onOpenChange(false)
    setFormData({
      instructions: '',
      deploymentType: null,
      collectionId: undefined,
    })
  }

  const isFormValid = formData.deploymentType !== null

  const getValidationMessage = () => {
    if (formData.deploymentType === null) {
      return 'Deployment type is required'
    }
    return ''
  }

  return (
    <ConfirmationModal
      open={open}
      onOpenChange={onOpenChange}
      title="Create your AI Agent"
      className="sm:max-w-2xl"
      primaryAction={{
        label: 'Create',
        onClick: handleSubmit,
        isLoading,
        disabled: !isFormValid,
        disabledMessage: getValidationMessage(),
      }}
      secondaryAction={{
        label: 'Close',
        onClick: handleClose,
        variant: 'outline',
      }}
    >
      <div className="flex flex-col">
        <div className="flex flex-col gap-2 mb-4">
          <div className="relative">
            <Textarea
              id="instructions"
              value={formData.instructions}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  instructions: e.target.value,
                }))
              }
              placeholder="Provide your brand and objective, we'll get you started with custom rules..."
              className="min-h-[200px] resize-none pr-8 pb-10"
            />
            {collections && collections.length > 0 && (
              <div className="absolute bottom-3 left-3">
                <Select
                  value={formData.collectionId ?? ''}
                  onValueChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      collectionId: value || undefined,
                    }))
                  }
                >
                  <SelectTrigger
                    className="h-auto w-auto min-w-0 gap-1 rounded-full border-0 bg-secondary px-3 py-2 text-xs font-medium shadow-none"
                    data-testid="feed-dropdown"
                  >
                    <SelectValue placeholder="Select feed" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={noFeedValue}>No product feed</SelectItem>
                    {collections.map((collection) => (
                      <SelectItem key={collection.id} value={collection.id}>
                        {collection.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="absolute bottom-2 right-2 w-2 h-2 opacity-60">
              <img src={dragIcon} alt="" className="w-full h-full" />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Label className="text-sm font-medium text-foreground">
            Deployment <span className="text-destructive">*</span>
          </Label>
          <DeploymentTypeCards
            selected={formData.deploymentType}
            onSelect={(deploymentType) =>
              setFormData((prev) => ({ ...prev, deploymentType }))
            }
          />
        </div>
      </div>
    </ConfirmationModal>
  )
}
